/**
 * **重大资产列表页**（R 域 R4 · `AC-R4.1`–`R4.3`）· 批 92
 *
 * 依据：`AC-R4.1`（入口 = 家底 → 资产 → 「重大资产」区块，直达 ≤2 跳）·
 * `AC-R4.2`（按 `type` 分组；行显七项；`hidden` 进折叠且可恢复）·
 * `AC-R4.3`（合计数 = `estimatedValue` 合计，`null` 不入分母）·
 * 原型 `16-重大资产_原型.html:159–263`。
 *
 * ## 🔴 页面只做三件事
 *
 * 1. 取数（`listMajorAssets` + `listLedgerMembers`）；
 * 2. 把分组的**判断**交给 `features/major-asset.ts`（`groupAssets` / `hiddenAssetsOf`）；
 * 3. 建资产时把草稿交给纯逻辑层校验与构造请求。
 *
 * ## ⚠️ 合计口径
 *
 * 分组头的「估值合计」是**展示**合计（只为回答"这一格有多大"），
 * 与 `AC-R4.3` 的净资产桶不是同一个数（后者由服务端 `composeNetWorth` 出，含汇率折算）。
 * 因此这里**不显示"小计"以外的任何汇总**，避免被当成净资产口径。
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoneyText, type Cents } from '@jiaying/shared';
import { useNavigate } from 'react-router-dom';
import { createMajorAsset, listLedgerMembers, listMajorAssets } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { useBaseCurrency } from '../app/useBaseCurrency.js';
import {
  buildCreateAssetRequest,
  emptyAssetDraft,
  groupAssets,
  hiddenAssetsOf,
  validateAssetDraft,
  type AssetFormDraft,
  type DraftErrors,
} from '../features/major-asset.js';
import {
  DISPOSED_SECTION_HINT,
  DISPOSED_SECTION_TITLE,
  EMPTY_LIST_TEXT,
  HIDDEN_SECTION_HINT,
  HIDDEN_SECTION_TITLE,
  LIST_ADD,
  LIST_SUBTITLE,
  LIST_TITLE,
  TYPE_HINT,
} from '../features/major-asset-labels.js';
import { MajorAssetRow } from '../ui/MajorAssetRow.js';
import { AssetFormDialog } from '../ui/MajorAssetDialogs.js';
import { Grid } from '../ui/Grid.js';

export function MajorAssetsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const baseCurrency = useBaseCurrency();

  const list = useQuery({ queryKey: ['major-assets'], queryFn: listMajorAssets });
  const members = useQuery({ queryKey: ['ledger-members'], queryFn: listLedgerMembers });

  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState<AssetFormDraft | null>(null);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [showDisposed, setShowDisposed] = useState(false);

  const create = useMutation({
    mutationFn: createMajorAsset,
    onSuccess: (detail) => {
      void qc.invalidateQueries({ queryKey: ['major-assets'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      setFormOpen(false);
      setDraft(null);
      // 🔴 建完直接进详情：用户接下来要做的（记购入 / 调估值）都在那一页
      navigate(`/major-assets/${encodeURIComponent(detail.id)}`);
    },
  });

  /** 打开"新建"：草稿的币种取**账本本币**（`null` 时不给默认值，见 `useBaseCurrency` 文件头） */
  const openCreate = () => {
    if (baseCurrency === null) return;
    const selfId = members.data?.find((m) => m.isSelf)?.userId ?? '';
    setDraft(emptyAssetDraft(baseCurrency, selfId));
    setErrors({});
    setFormOpen(true);
  };

  const submit = () => {
    if (draft === null) return;
    const e = validateAssetDraft(draft);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    create.mutate(buildCreateAssetRequest(draft));
  };

  const items = list.data?.items ?? [];
  const disposed = list.data?.disposed ?? [];
  const groups = groupAssets(items);
  const hidden = hiddenAssetsOf(items);

  return (
    <Stack spacing={2} data-testid="major-assets-page">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {LIST_TITLE}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {LIST_SUBTITLE}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={openCreate}
          disabled={baseCurrency === null || formOpen}
          data-testid="ma-add"
        >
          {LIST_ADD}
        </Button>
      </Stack>

      {create.isError && (
        <Alert severity="error" data-testid="ma-create-error">
          创建失败：{create.error instanceof ApiError ? create.error.message : String(create.error)}
        </Alert>
      )}

      {list.isPending && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {list.isError && <Alert severity="error">列表读取失败：{String(list.error)}</Alert>}

      {list.isSuccess && items.length === 0 && disposed.length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" data-testid="ma-empty">
              {EMPTY_LIST_TEXT}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* 主分组：房产 / 车辆 / 收藏 —— 空组不渲染（`AC-R4.2`） */}
      {groups.map((g) => (
        <Card key={g.type} data-testid={`ma-group-${g.type}`}>
          <CardContent sx={{ pb: 0.5 }}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {g.label}（{g.items.length}）
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }} noWrap>
                {TYPE_HINT[g.type]}
              </Typography>
              {/* 估值合计：`null` 的**不进分母、也不进合计**（`AC-R5.3`） */}
              <Typography variant="caption" color="text.secondary" data-testid={`ma-group-sum-${g.type}`}>
                估值合计 {sumTextOf(g.items.map((i) => i.estimatedValue), baseCurrencyOf(g.items))}
              </Typography>
            </Stack>
          </CardContent>
          <Divider />
          <List dense disablePadding sx={{ px: 0.5, py: 0.5 }}>
            {g.items.map((v) => (
              <MajorAssetRow
                key={v.id}
                view={v}
                onClick={() => navigate(`/major-assets/${encodeURIComponent(v.id)}`)}
              />
            ))}
          </List>
        </Card>
      ))}

      <Grid cols={2} sx={{ alignItems: 'start' }}>
        {/* 已隐藏：折叠，可展开（`AC-R4.2`：hidden=1 进折叠且**可恢复** —— 恢复在详情/编辑里） */}
        <Card data-testid="ma-hidden-section">
          <CardContent sx={{ pb: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {HIDDEN_SECTION_TITLE}（{hidden.length}）
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {HIDDEN_SECTION_HINT}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
          {hidden.length > 0 && (
            <>
              <Divider />
              <List dense disablePadding sx={{ px: 0.5, py: 0.5 }}>
                {hidden.map((v) => (
                  <MajorAssetRow
                    key={v.id}
                    view={v}
                    onClick={() => navigate(`/major-assets/${encodeURIComponent(v.id)}`)}
                  />
                ))}
              </List>
            </>
          )}
        </Card>

        {/* 已处置：只读历史（`AC-R8.3`）—— 默认折叠，展开后点得进详情但无动作 */}
        <Card data-testid="ma-disposed-section">
          <CardContent sx={{ pb: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {DISPOSED_SECTION_TITLE}（{disposed.length}）
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {DISPOSED_SECTION_HINT}
                </Typography>
              </Box>
              {disposed.length > 0 && (
                <Button size="small" onClick={() => setShowDisposed((s) => !s)}>
                  {showDisposed ? '收起' : '展开'}
                </Button>
              )}
            </Stack>
          </CardContent>
          {showDisposed && disposed.length > 0 && (
            <>
              <Divider />
              <List dense disablePadding sx={{ px: 0.5, py: 0.5 }}>
                {disposed.map((v) => (
                  <MajorAssetRow
                    key={v.id}
                    view={v}
                    onClick={() => navigate(`/major-assets/${encodeURIComponent(v.id)}`)}
                  />
                ))}
              </List>
            </>
          )}
          {disposed.length === 0 && (
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                还没有处置过的资产
              </Typography>
            </CardContent>
          )}
        </Card>
      </Grid>

      {draft !== null && (
        <AssetFormDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          draft={draft}
          errors={errors}
          busy={create.isPending}
          mode="create"
          members={members.data ?? []}
          onChange={(patch) => setDraft((d) => (d === null ? d : { ...d, ...patch }))}
          onSubmit={submit}
        />
      )}

      {members.isError && (
        <Alert severity="warning">
          成员名单读取失败 —— 归属人选择会为空：{String(members.error)}
        </Alert>
      )}
    </Stack>
  );
}

/** 一组行的币种（组内币种一致是常见情形；不一致时退回第一行 —— 合计只作展示） */
function baseCurrencyOf(items: readonly { currency: string }[]): string {
  return items[0]?.currency ?? '';
}

/**
 * 估值合计文本 —— 🔴 `null`（未填估值）**不进分母也不当 0**（`AC-R5.3`）。
 *
 * ⚠️ 组内跨币种时**不可相加**（`DR-004`：余额原币、不跨币相加）⇒ 直接提示"多币种"，
 * 不给出一个假合计。
 */
function sumTextOf(values: readonly (number | null)[], currency: string): string {
  const known = values.filter((v): v is number => v !== null);
  if (known.length === 0) return '—（均未填估值）';
  if (currency === '') return '—';
  const total = known.reduce((a, b) => a + b, 0);
  const text = formatMoneyText(total as Cents, currency);
  const missing = values.length - known.length;
  return missing > 0 ? `${text}（${String(missing)} 项未填）` : text;
}
